import { ReactNode } from 'react';

import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import {
    addDays,
    addMonths,
    addWeeks,
    differenceInDays,
    differenceInMonths,
    differenceInWeeks,
    startOfDay
} from 'date-fns';
import { FormattedMessage } from 'react-intl';

type TPresetDate = { key: string; label: ReactNode; getDate: () => Date };

export const PRESET_DATES: TPresetDate[] = [
    {
        key: '4-weeks',
        label: (
            <FormattedMessage
                id="_.choose_amount.early_repayment.four_weeks"
                defaultMessage="4 weeks"
            />
        ),
        getDate: () => addWeeks(new Date(), 4)
    },
    {
        key: '6-months',
        label: (
            <FormattedMessage
                id="_.choose_amount.early_repayment.six_months"
                defaultMessage="6 months"
            />
        ),
        getDate: () => addMonths(new Date(), 6)
    },
    {
        key: '9-months',
        label: (
            <FormattedMessage
                id="_.choose_amount.early_repayment.nine_months"
                defaultMessage="9 months"
            />
        ),
        getDate: () => addMonths(new Date(), 9)
    }
];

export const getOfferDurationPreset = (
    selectedOffer: TFormalOffer
): TPresetDate => {
    const durationMonths = getDurationInMonths(selectedOffer.duration!);
    return {
        key: 'full-duration',
        label: (
            <FormattedMessage
                id="_.choose_amount.early_repayment.full_duration"
                defaultMessage="{months} months"
                values={{ months: durationMonths }}
            />
        ),
        getDate: () => addDays(new Date(), selectedOffer.duration!.amount)
    };
};

// Converts a yyyy-MM-dd date string to a human-readable relative duration from today,
// using the largest fitting unit: months > weeks > days (e.g. "3 months and 10 days").
export const formatDurationFromDate = (selectedDate: string): string => {
    const today = startOfDay(new Date());
    const target = new Date(selectedDate);
    const totalDays = differenceInDays(target, today);

    if (totalDays < 1) return '0 days';

    const months = differenceInMonths(target, today);

    if (months >= 1) {
        const dateAfterMonths = addMonths(today, months);
        const remainingDays = differenceInDays(target, dateAfterMonths);

        if (remainingDays === 0) {
            return `${months} month${months !== 1 ? 's' : ''}`;
        }
        return `${months} month${months !== 1 ? 's' : ''} and ${remainingDays} day${remainingDays !== 1 ? 's' : ''}`;
    }

    const weeks = differenceInWeeks(target, today);

    if (weeks >= 1) {
        const dateAfterWeeks = addWeeks(today, weeks);
        const remainingDays = differenceInDays(target, dateAfterWeeks);

        if (remainingDays === 0) {
            return `${weeks} week${weeks !== 1 ? 's' : ''}`;
        }
        return `${weeks} week${weeks !== 1 ? 's' : ''} and ${remainingDays} day${remainingDays !== 1 ? 's' : ''}`;
    }

    return `${totalDays} day${totalDays !== 1 ? 's' : ''}`;
};
